import type { ButtonProps } from 'app/design/primitives/button';
import { Button } from 'app/design/primitives/button';
import { Page } from 'app/design/primitives/page';
import { Accessory } from 'app/design/primitives/page/accessory';
import { ProgressIndicator } from 'app/design/primitives/progress-indicator';
import { Text } from 'app/design/primitives/text';
import { useRoute } from 'app/hooks/navigation';
import { useOnboarding } from 'app/hooks/use-onboarding';
import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResponsiveValue } from '@shopify/restyle/dist/types';
import type { Theme } from 'app/shopify';

interface OnboardingLayoutProps {
  primary: Pick<ButtonProps, 'disabled' | 'loading' | 'onPress'>;
  activeColor?: ResponsiveValue<keyof Theme['colors'], Theme['breakpoints']>;
  titleKey?: string;
  showProgress?: boolean;
}

export function OnboardingLayout({
  children,
  primary,
  activeColor,
  titleKey,
  showProgress = true,
}: PropsWithChildren<OnboardingLayoutProps>) {
  const route = useRoute();
  const { t } = useTranslation('onboarding');
  const { current, max } = useOnboarding();

  return (
    <Page
      accessory={
        <Accessory>
          <Button
            marginHorizontal="spacing.general.x4"
            testID="onboarding-primary-button"
            variant="primary"
            {...primary}
          >
            <Text color="surface.default" textAlign="center">
              {current === max - 1 ? t('confirm') : t('nextStep')}
            </Text>
          </Button>
        </Accessory>
      }
    >
      {showProgress ? <ProgressIndicator activeColor={activeColor} index={current} length={max} /> : null}

      <Text
        fontWeight="600"
        marginBottom="spacing.general.x4"
        marginTop="spacing.general.x8"
        textAlign="center"
        variant="headerXSmall"
      >
        {t(titleKey ?? 'title', { ns: route.name })}
      </Text>

      {children}
    </Page>
  );
}
